import { EntityType } from "@mapmycustomers/shared";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";

import TourDescriptor from "@app/component/flows/types/TourDescriptor";
import waitForElement from "@app/component/flows/utils/waitForElement";
import Path from "@app/enum/Path";
import { fetchPins } from "@app/scene/map/store/mapMode/actions";
import { createAndCondition, createSimpleCondition } from "@app/util/filters/utils";

import { messages } from "./messages";
import {
  BULK_EDIT_VALUE_BOX_SELECTOR,
  BULK_EDIT_VALUE_INPUT_SELECTOR,
  DEFAULT_T_GROUP_VALUE,
  delay,
  deselectMapTool,
  getTGroupField,
  initializeJasperBulkEditTourOnBefore,
  openCompanyFilterTypeMenuOnBefore,
  T_GROUP_FIELD_NAME,
} from "./utils";

// The "Select filter type" primary button in the map filter popover (NoFiltersState — shown
// because step 1 cleared all filters, so it is the only primary button in the popover).
const SELECT_FILTER_TYPE_BUTTON = ".mmc-data_map-filter-popover .ant-btn-primary";
// antd Dropdown menu item whose key is the entity type — EntityType.COMPANY = "accounts"
// (the other keys are "universal", "contacts", "deals", so the $= suffix match is unambiguous)
const COMPANY_TYPE_MENU_ITEM = '.ant-dropdown-menu-item[data-menu-id$="accounts"]';
const APPLY_BUTTON = "[class*=BulkEdit_popoverContainer] .ant-btn-primary";

export const jasperBulkEditTourConfiguration: TourDescriptor = {
  id: "mmc-jasper-bulk-edit",
  steps: [
    {
      id: "intro",
      description: messages.step01Description,
      goToPathBefore: Path.MAP,
      onBefore: initializeJasperBulkEditTourOnBefore,
      title: messages.step01,
    },
    {
      id: "open-filter",
      description: messages.step02Description,
      options: {
        disableInteraction: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "#map-filter-button",
      title: messages.step02,
      waitForEvent: {
        eventType: "internal",
        properties: { visible: true },
        selector: "Clicked: Map -> Filters -> Filters",
      },
    },
    {
      id: "company-filter-type",
      description: messages.step03Description,
      onBefore: async () =>
        openCompanyFilterTypeMenuOnBefore(SELECT_FILTER_TYPE_BUTTON, COMPANY_TYPE_MENU_ITEM),
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "right",
      selector: COMPANY_TYPE_MENU_ITEM,
      title: messages.step03,
      waitForEvent: {
        eventType: "internal",
        selector: "Process: Map -> Filters -> Select Filter type -> Company",
      },
    },
    {
      id: "select-field",
      description: messages.step04Description,
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "right",
      // data-entity-type carries the EntityType enum value: "accounts" for companies
      selector: "[data-entity-type=accounts] [class*=FieldColumn_fieldNameSelectBox]",
      title: messages.step04,
      waitForEvent: {
        eventType: "internal",
        properties: { field: T_GROUP_FIELD_NAME },
        selector: "Using: Map -> Filters -> Company Filters -> Select Field",
      },
    },
    {
      id: "filter-value",
      description: messages.step05Description,
      onBefore: async ({ store }) => {
        const field = getTGroupField();
        if (!field) {
          return;
        }
        // pre-fill the value via redux — the popover re-syncs its draft from the store
        store.dispatch(
          fetchPins.request({
            request: {
              filter: {
                [EntityType.COMPANY]: createAndCondition([
                  createSimpleCondition(
                    field.filterName,
                    FilterOperator.EQUALS,
                    DEFAULT_T_GROUP_VALUE
                  ),
                ]),
              },
            },
          })
        );
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "right",
      selector: "[data-entity-type=accounts] .mmc-number-field",
      title: messages.step05,
      // No Next button here: the tooltip is anchored inside the filter popover, and antd
      // closes the popover on document mousedown — a press on Next would unmount the anchor
      // (destroyTooltipOnHide) and the tooltip with it before the click lands. Closing the
      // popover is the advance signal instead; the same handler applies the filter draft.
      waitForEvent: {
        eventType: "internal",
        properties: { visible: false },
        selector: "Clicked: Map -> Filters -> Filters",
      },
    },
    {
      id: "lasso-tool",
      description: messages.step06Description,
      options: {
        disableInteraction: false,
        showBackground: true,
        showButtons: false,
      },
      position: "left",
      selector: "[data-tool-btn=lasso]",
      title: messages.step06,
      waitForEvent: {
        eventType: "click",
        selector: "[data-tool-btn=lasso]",
      },
    },
    {
      id: "lasso-draw",
      description: messages.step07Description,
      key: "lasso-draw",
      title: messages.step07,
    },
    {
      id: "lasso-draw-2",
      description: messages.step07DescriptionA,
      key: "lasso-draw",
      onShow: async () => {
        return { action: "navigate", stepId: "selection-count" };
      },
      options: {
        disableInteraction: false, // it must be here, otherwise lasso won't work
      },
      selector: "[class*=LassoToolPanel_content]",
    },
    {
      id: "selection-count",
      description: messages.step08Description,
      position: "bottom",
      selector: "[class*=LassoToolPanel_content]",
      title: messages.step08,
    },
    {
      id: "bulk-edit-button",
      description: messages.step09Description,
      options: {
        disableInteraction: false,
        showButtons: false,
      },
      position: "bottom",
      selector: "[data-selection-button=bulk-edit]",
      title: messages.step09,
      waitForEvent: {
        eventType: "click",
        selector: "[data-selection-button=bulk-edit]",
      },
    },
    {
      id: "bulk-edit-field",
      description: messages.step10Description,
      onBefore: async () => {
        // let the popover animation complete
        await delay(100);
      },
      options: {
        disableInteraction: false,
        showBackground: false,
        showButtons: false,
      },
      position: "right",
      selector: "[class*=BulkEditForm_dropdown]",
      title: messages.step10,
      waitForEvent: {
        eventType: "internal",
        properties: { field: T_GROUP_FIELD_NAME },
        selector: "Using: Map -> Lasso -> Created Shape -> Bulk Edit -> Select Field",
      },
    },
    {
      id: "bulk-edit-value",
      canGoNext: () =>
        !!document.querySelector<HTMLInputElement>(BULK_EDIT_VALUE_INPUT_SELECTOR)?.value,
      description: messages.step11Description,
      onBefore: async () =>
        new Promise<void>((resolve) => {
          waitForElement(BULK_EDIT_VALUE_INPUT_SELECTOR, () => {
            document.querySelector<HTMLInputElement>(BULK_EDIT_VALUE_INPUT_SELECTOR)?.focus();
            resolve();
          });
        }),
      options: {
        disableInteraction: false,
        // anchored inside the Bulk Edit popover — without this, pressing Next closes it
        preventMouseDown: true,
        showBackground: true,
      },
      position: "right",
      // the .ant-input-number box only — the FieldEditor container would also highlight
      // the "Change value to" label, the "Clear all values" checkbox, and the note below
      selector: BULK_EDIT_VALUE_BOX_SELECTOR,
      title: messages.step11,
    },
    {
      id: "apply",
      description: messages.step12Description,
      // Apply keeps the lasso tool active (refreshLassoMode) — deselect it so the tour
      // leaves no lasso mode behind. Runs on both paths that complete this last step
      // (clicking Apply itself, via waitForEvent below, and clicking Done).
      onAfter: async ({ store }) => {
        deselectMapTool(store);
      },
      options: {
        disableInteraction: false,
        // anchored inside the Bulk Edit popover — without this, pressing Done closes it
        preventMouseDown: true,
        showBackground: true,
      },
      position: "bottom",
      selector: APPLY_BUTTON,
      title: messages.step12,
      // last step shows "Done"; also complete when the user clicks Apply itself,
      // otherwise the popover closes on success and Done becomes unreachable
      waitForEvent: {
        eventType: "click",
        selector: APPLY_BUTTON,
      },
    },
  ],
};
